import React, { useState } from 'react';
import { db, storage } from './firebaseconfig';
import './Style/addsaloon.css';
import { doc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const AddSalon = () => {
  // Initialize salon details with extra fields for open status, Maxdiscount, averageRating.
  const initialSalonDetails = {
    name: '',
    type: '',
    address: '',
    latitude: '',
    longitude: '',
    openingHours: '',
    closingHours: '',
    phoneNumber: '',
    barberEmail: '',
    about: '',
    percentage: '',
    Maxdiscount: '',
    averageRating: '',
    open: 'open',
  };

  const [salonDetails, setSalonDetails] = useState(initialSalonDetails);
  const [saloonId, setSaloonId] = useState('');
  const [serviceTitle, setServiceTitle] = useState('');
  const [serviceName, setServiceName] = useState('');
  const [servicePrice, setServicePrice] = useState('');
  const [serviceTime, setServiceTime] = useState('');
  // New state variable for service percentage
  const [servicePercentage1, setServicePercentage1] = useState('');
  // New state variable for service gender when salon is unisex
  const [serviceGender, setServiceGender] = useState('');
  const [services, setServices] = useState([]);
  const [isAddingServices, setIsAddingServices] = useState(false);
  const [serviceIdCounter, setServiceIdCounter] = useState(1);
  const [images, setImages] = useState(Array(6).fill(null)); // Up to 6 images
  const [isLoading, setIsLoading] = useState(false);

  // Update salon details from inputs
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSalonDetails((prevDetails) => ({
      ...prevDetails,
      [name]: value,
    }));
  };

  // Handle image file changes
  const handleImageChange = (index, e) => {
    if (e.target.files && e.target.files[0]) {
      const newImages = [...images];
      newImages[index] = e.target.files[0];
      setImages(newImages);
    }
  };

  // Add a new service detail
  const handleAddService = () => {
    const newService = {
      id: serviceIdCounter,
      name: serviceName || null,
      price: servicePrice ? parseFloat(servicePrice) : null,
      time: serviceTime || null,
      percentage: servicePercentage1 || null,
    };

    if (salonDetails.type === 'unisex') {
      if (!serviceGender) {
        alert('Please select a gender for this service.');
        return;
      }
      // Check if a service category with the same title exists.
      const existingServiceIndex = services.findIndex(
        (service) => service.title === serviceTitle
      );
      if (existingServiceIndex !== -1) {
        const updatedServices = [...services];
        if (serviceGender === 'male') {
          updatedServices[existingServiceIndex].male.push(newService);
        } else {
          updatedServices[existingServiceIndex].female.push(newService);
        }
        setServices(updatedServices);
      } else {
        // Create a new service category with separate arrays for male and female.
        if (serviceGender === 'male') {
          setServices([
            ...services,
            { title: serviceTitle, male: [newService], female: [] },
          ]);
        } else {
          setServices([
            ...services,
            { title: serviceTitle, male: [], female: [newService] },
          ]);
        }
      }
    } else {
      // For non-unisex salons, store all services in a single array.
      const existingServiceIndex = services.findIndex(
        (service) => service.title === serviceTitle
      );
      if (existingServiceIndex !== -1) {
        const updatedServices = [...services];
        updatedServices[existingServiceIndex].services.push(newService);
        setServices(updatedServices);
      } else {
        setServices([
          ...services,
          { title: serviceTitle, services: [newService] },
        ]);
      }
    }

    setServiceIdCounter((prevCounter) => prevCounter + 1);
    // Reset service input fields
    setServiceName('');
    setServicePrice('');
    setServiceTime('');
    setServicePercentage1('');
    setServiceGender('');
    setIsAddingServices(false);
  };

  const handleAddServiceDetail = () => {
    if (serviceTitle) {
      setIsAddingServices(true);
    } else {
      alert('Please enter a service title.');
    }
  };

  // Upload images to Firebase Storage and return their URLs
  const uploadImages = async () => {
    const imageUrls = [];
    for (const image of images) {
      if (image) {
        const imageRef = ref(storage, `saloonImages/${saloonId}/${image.name}`);
        await uploadBytes(imageRef, image);
        const imageUrl = await getDownloadURL(imageRef);
        imageUrls.push(imageUrl);
      }
    }
    return imageUrls;
  };

  // Final submit function to store data in Firebase
  const handleSubmit = async () => {
    if (!saloonId) {
      alert('Please enter a salon ID.');
      return;
    }

    setIsLoading(true);
    try {
      // Upload images and gather URLs
      const imageUrls = await uploadImages();

      // Prepare the salon data (filter out empty fields)
      const newSalonData = {
        ...Object.fromEntries(
          Object.entries(salonDetails).filter(([_, v]) => v !== undefined && v !== '')
        ),
        images: imageUrls,
        services: services.map((service) => {
          if (salonDetails.type === 'unisex') {
            return {
              title: service.title,
              male: service.male,
              female: service.female,
            };
          }
          return {
            title: service.title,
            services: service.services,
          };
        }),
      };

      // Convert Maxdiscount and averageRating to integers if provided
      if (newSalonData.Maxdiscount) {
        newSalonData.Maxdiscount = parseInt(newSalonData.Maxdiscount);
      }
      if (newSalonData.averageRating) {
        newSalonData.averageRating = parseInt(newSalonData.averageRating);
      }

      console.log('Submitting data:', newSalonData);
      await setDoc(doc(db, 'saloons', saloonId), newSalonData);
      alert('Salon added successfully!');

      // Reset all fields for a new entry
      setSalonDetails(initialSalonDetails);
      setSaloonId('');
      setServices([]);
      setServiceTitle('');
      setServiceName('');
      setServicePrice('');
      setServiceTime('');
      setServicePercentage1('');
      setServiceGender('');
      setImages(Array(6).fill(null));
    } catch (error) {
      console.error('Error adding salon data: ', error);
      alert('Error adding salon data.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="container"
      style={{
        padding: '20px',
        backgroundColor: '#f4f4f4',
        display: 'flex',
        gap: '20px',
      }}
    >
      {/* Left side: Form */}
      <div
        className="form-container"
        style={{
          flex: 1,
          background: '#fff',
          padding: '20px',
          borderRadius: '8px',
        }}
      >
        <h1>Add New Salon</h1>
        <form>
          <input
            type="text"
            name="saloonId"
            placeholder="Salon ID"
            value={saloonId}
            onChange={(e) => setSaloonId(e.target.value)}
            required
          />
          <input
            type="text"
            name="name"
            placeholder="Salon Name"
            value={salonDetails.name}
            onChange={handleInputChange}
            required
          />

          {/* Upload up to 6 images */}
          {images.map((image, index) => (
            <div key={index}>
              <label>Upload Image {index + 1}</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageChange(index, e)}
              />
            </div>
          ))}

          <input
            type="text"
            name="type"
            placeholder="Salon Type (male, female, unisex)"
            value={salonDetails.type}
            onChange={handleInputChange}
            required
          />
          <input
            type="text"
            name="address"
            placeholder="Address"
            value={salonDetails.address}
            onChange={handleInputChange}
            required
          />
          <input
            type="text"
            name="latitude"
            placeholder="Latitude"
            value={salonDetails.latitude}
            onChange={handleInputChange}
            required
          />
          <input
            type="text"
            name="longitude"
            placeholder="Longitude"
            value={salonDetails.longitude}
            onChange={handleInputChange}
            required
          />
          <input
            type="text"
            name="openingHours"
            placeholder="Opening Hours"
            value={salonDetails.openingHours}
            onChange={handleInputChange}
            required
          />
          <input
            type="text"
            name="closingHours"
            placeholder="Closing Hours"
            value={salonDetails.closingHours}
            onChange={handleInputChange}
            required
          />
          <input
            type="text"
            name="phoneNumber"
            placeholder="Phone Number"
            value={salonDetails.phoneNumber}
            onChange={handleInputChange}
            required
          />
          <input
            type="email"
            name="barberEmail"
            placeholder="Barber's Email"
            value={salonDetails.barberEmail}
            onChange={handleInputChange}
            required
          />
          <input
            type="text"
            name="about"
            placeholder="About the Salon"
            value={salonDetails.about}
            onChange={handleInputChange}
          />

          {/* Commission Percentage Field */}
          <h3>Commission Percentage</h3>
          <input
            type="number"
            name="percentage"
            placeholder="Commission Percentage"
            value={salonDetails.percentage}
            onChange={handleInputChange}
            required
          />

          {/* Max Discount Field */}
          <h3>Max Discount</h3>
          <input
            type="number"
            name="Maxdiscount"
            placeholder="Max Discount"
            value={salonDetails.Maxdiscount}
            onChange={handleInputChange}
            required
          />

          {/* Average Rating Field */}
          <h3>Average Rating</h3>
          <input
            type="number"
            name="averageRating"
            placeholder="Average Rating"
            value={salonDetails.averageRating}
            onChange={handleInputChange}
            required
          />

          {/* Open Status Field */}
          <h3>Open Status</h3>
          <select name="open" value={salonDetails.open} onChange={handleInputChange} required>
            <option value="open">Open</option>
            <option value="close">Close</option>
          </select>

          {/* Service Section */}
          <h2>Add Service</h2>
          {!isAddingServices ? (
            <>
              <input
                type="text"
                placeholder="Service Title"
                value={serviceTitle}
                onChange={(e) => setServiceTitle(e.target.value)}
                required
              />
              <button type="button" onClick={handleAddServiceDetail}>
                Add Service Details
              </button>
            </>
          ) : (
            <>
              <input
                type="text"
                placeholder="Service Name"
                value={serviceName}
                onChange={(e) => setServiceName(e.target.value)}
                required
              />
              <input
                type="number"
                placeholder="Price"
                value={servicePrice}
                onChange={(e) => setServicePrice(e.target.value)}
                required
              />
              <input
                type="text"
                placeholder="Time Required"
                value={serviceTime}
                onChange={(e) => setServiceTime(e.target.value)}
                required
              />
              {/* Service Percentage Field */}
              <input
                type="text"
                placeholder="Service Percentage"
                value={servicePercentage1}
                onChange={(e) => setServicePercentage1(e.target.value)}
                required
              />
              {salonDetails.type === 'unisex' && (
                <select
                  value={serviceGender}
                  onChange={(e) => setServiceGender(e.target.value)}
                  required
                >
                  <option value="" disabled>
                    Select Gender
                  </option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              )}
              <button type="button" onClick={handleAddService}>
                Add Service
              </button>
            </>
          )}
        </form>
      </div>

      {/* Right side: Review panel */}
      <div
        className="summary-container"
        style={{
          flex: 1,
          background: '#fff',
          padding: '20px',
          borderRadius: '8px',
        }}
      >
        <h2>Review Details</h2>
        <div>
          <p>
            <strong>Salon ID:</strong> {saloonId}
          </p>
          <p>
            <strong>Name:</strong> {salonDetails.name}
          </p>
          <p>
            <strong>Type:</strong> {salonDetails.type}
          </p>
          <p>
            <strong>Address:</strong> {salonDetails.address}
          </p>
          <p>
            <strong>Latitude:</strong> {salonDetails.latitude}
          </p>
          <p>
            <strong>Longitude:</strong> {salonDetails.longitude}
          </p>
          <p>
            <strong>Opening Hours:</strong> {salonDetails.openingHours}
          </p>
          <p>
            <strong>Closing Hours:</strong> {salonDetails.closingHours}
          </p>
          <p>
            <strong>Phone Number:</strong> {salonDetails.phoneNumber}
          </p>
          <p>
            <strong>Barber's Email:</strong> {salonDetails.barberEmail}
          </p>
          <p>
            <strong>About:</strong> {salonDetails.about}
          </p>
          <p>
            <strong>Commission Percentage:</strong> {salonDetails.percentage}
          </p>
          <p>
            <strong>Max Discount:</strong> {salonDetails.Maxdiscount}
          </p>
          <p>
            <strong>Average Rating:</strong> {salonDetails.averageRating}
          </p>
          <p>
            <strong>Open Status:</strong> {salonDetails.open}
          </p>
          {services.length > 0 && (
            <div>
              <h3>Services</h3>
              {services.map((service, idx) => (
                <div key={idx}>
                  <p>
                    <strong>Title:</strong> {service.title}
                  </p>
                  {salonDetails.type === 'unisex' ? (
                    <>
                      <p>
                        <strong>Male Services:</strong>{' '}
                        {JSON.stringify(service.male)}
                      </p>
                      <p>
                        <strong>Female Services:</strong>{' '}
                        {JSON.stringify(service.female)}
                      </p>
                    </>
                  ) : (
                    <p>
                      <strong>Services:</strong>{' '}
                      {JSON.stringify(service.services)}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
          {images.some((img) => img) && (
            <div>
              <h3>Images Selected:</h3>
              {images.map(
                (img, idx) => img && <p key={idx}>{img.name}</p>
              )}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isLoading}
          className="submit-button"
        >
          {isLoading ? 'Submitting...' : 'Agree and Submit'}
        </button>
      </div>
    </div>
  );
};

export default AddSalon;
